// @ts-nocheck
import classnames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { pushParams } from 'dpl/actions/query_params';
import BuyersGuideModule from 'dpl/breeder_search_results/components/BuyersGuideModule';
import ResultsHeading from 'dpl/breeder_search_results/components/ResultsHeading';
import ResultsHeadingSkeleton from 'dpl/breeder_search_results/components/skeletons/ResultsHeadingSkeleton';
import BreederCard from 'dpl/breeder_search_results/mobile/components/Card';
import { breederProfilesResourceDefinition } from 'dpl/breeder_search_results/utils/queryDefinitions';
import { GDButtonV1 } from 'dpl/common/core/GDButton/GDButtonV1';
import Paginator from 'dpl/common/design_system/Paginator';
import LoadingWrapper from 'dpl/components/LoadingWrapper';
import SEOFooter from 'dpl/components/SEOFooter';
import { ANALYTICS_EVENTS, ANALYTICS_VIEWS } from 'dpl/constants/analytics';
import {
  FILTER_AGE,
  FILTER_AVAILABILITY,
  FILTER_COAT,
  FILTER_COLOR,
  FILTER_DELIVERY_METHODS,
  FILTER_DISTANCE_IN_MILES,
  FILTER_EXTRAS,
  FILTER_GENDER,
  FILTER_GENERATION,
  FILTER_HEALTH_TIER_LEVELS,
  FILTER_LATITUDE,
  FILTER_LOCATION_NAME,
  FILTER_LONGITUDE,
  FILTER_POSTAL_CODE,
  FILTER_PRICE,
  FILTER_SIZE,
  FILTER_TAIL,
  PAGE,
  RESULTS_TYPE,
  SORT,
  UTM_MEDIUM
} from 'dpl/constants/query_params';
import BreederProfileHelpBoxContainer from 'dpl/containers/BreederProfile/Index/BreederProfileHelpBoxContainer';
import BreedLocationInfo from 'dpl/containers/BreedLocationInfo';
import ResultsTypeSelector from 'dpl/puppy_search_results/components/ResultsTypeSelector';
import SearchResults from 'dpl/puppy_search_results/components/SearchResults';
import {
  NON_LOCATION_SORT_OPTIONS,
  RESULTS_TYPES,
  SEARCH_ALERTS_FEATURE_KEY
} from 'dpl/puppy_search_results/utils/constants';
import {
  puppiesResourceDefinition,
  puppyAndBreederCountsResourceDefinition
} from 'dpl/puppy_search_results/utils/queryDefinitions';
import SearchResultsContext from 'dpl/puppy_search_results/utils/SearchResultsContext';
import ExposedFilters from 'dpl/search_result_filters/components/ExposedFilters';
import useSearchFiltering from 'dpl/search_result_filters/hooks/useSearchFiltering';
import useSearchLocation from 'dpl/search_result_filters/hooks/useSearchLocation';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { FOREVER_DURATION, useSnoozed } from 'dpl/shared/hooks/useSnoozed';
import { scrollTo } from 'dpl/shared/utils';
import { removeEmptyValues } from 'dpl/shared/utils/object';
import { setAnalyticsOrigin } from 'dpl/shared/utils/setAnalyticsOrigin';
import { deliveryMethodOptionsResourceDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';
import {
  collectTrackedExperiments,
  sendRudderstackEvent
} from 'dpl/util/analytics';
import { removeFromUrl } from 'dpl/util/queryString';
import { BREED_TYPES } from 'dpl/components/BreedSearch/util';

import BreedContentModule from './BreedContentModule';
import BuyerAppQRModule from './BuyerAppQRModule';
import PuppyCard from './Card';
import FAQModule from './FAQModule';
import FeaturedBreedersModule from './FeaturedBreedersModule';
import NearbyOwnerStoriesModule from './NearbyOwnerStoriesModule';
import RelatedBreedsModule from './RelatedBreedsModule';
import SearchResultsSkeleton from './skeletons/SearchResultsSkeleton';

const BROWSE_PARAMS = [PAGE];
const BREEDER_RESULTS_PER_PAGE = 42;

function mapParamsBreedAttributes(paramValues = '', breedAttributes: any[]) {
  return paramValues
    .split(',')
    .map(p => breedAttributes.find(({ value }) => value === Number(p)))
    .filter(p => p);
}

function PuppyResultCard({
  result,
  analytics,
  hideDistance,
  trackInView
}: {
  result: any;
  analytics: any;
  hideDistance: boolean;
  trackInView: () => void;
}) {
  return (
    <PuppyCard
      className="pb3"
      puppy={result}
      analytics={analytics}
      hideDistance={hideDistance}
      view={ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS}
      trackInView={trackInView}
    />
  );
}

function BreederResultCard({
  result,
  analytics,
  trackInView
}: {
  result: any;
  analytics: any;
  trackInView: () => void;
}) {
  return (
    <BreederCard
      profile={result}
      analytics={analytics}
      trackInView={trackInView}
    />
  );
}

export default function TabbedLayout() {
  const {
    breedSlug,
    itemsPerPage,
    overPopulatedBreed,
    breedId,
    breedName,
    breedNamePlural,
    citySlug,
    locationTitle,
    breedColors,
    puppyPriceRanges,
    breedSizes,
    breedCoat,
    breedTail,
    breedGeneration,
    breedType,
    emailListJoinedAt,
    preappliedFilters: { stateSlug, cityName, size } = {},
    pbo: serverPbo,
    breed_search_attribute_filter: breedSearchAttributeFilter
  } = useSelector(({ server }: any) => server);

  const paidPage = useSelector(({ queryParams }: any) => {
    return ['cpc', 'cpa', 'cta'].includes(queryParams[UTM_MEDIUM]);
  });

  const params = useSelector(({ queryParams }: any) => queryParams);
  const prevParamsRef = useRef(params);
  const {
    value: { data: deliveryMethodOptions }
  } = useResourceQuery(deliveryMethodOptionsResourceDefinition(true));

  const { resultsType, clearFilters, isFiltersModalOpen } =
    useSearchFiltering();

  const { isSnoozed, _ } = useSnoozed();

  const isToggleHidden =
    isSnoozed(`${SEARCH_ALERTS_FEATURE_KEY}-${breedId}`, FOREVER_DURATION) ||
    emailListJoinedAt;

  const currentPage = Number(params[PAGE]) || 1;
  const isPuppiesTab = resultsType === RESULTS_TYPES.PUPPIES;
  const isBreedersTab = resultsType === RESULTS_TYPES.BREEDERS;

  // if pbo, only show breeders with payment activity
  const pbo = serverPbo || paidPage;

  const dispatch = useDispatch();

  // The updated breeder search results layout renders 3 items per row on desktop,
  // so the results per page must be adjusted to be a multiple of 3 (42 instead of 40)
  // to avoid rendering a single item in the last row.
  const resultsPerPage = isBreedersTab
    ? BREEDER_RESULTS_PER_PAGE
    : itemsPerPage;

  const searchQueryDefinition = isPuppiesTab
    ? puppiesResourceDefinition
    : breederProfilesResourceDefinition;

  const profilesQueryDefinition = {
    ...searchQueryDefinition(
      breedSlug,
      resultsPerPage,
      currentPage,
      removeEmptyValues({
        postal_code: params[FILTER_POSTAL_CODE],
        lat: params[FILTER_LATITUDE],
        long: params[FILTER_LONGITUDE],
        location: params[FILTER_LOCATION_NAME],
        state: stateSlug,
        city: cityName,
        distance_in_miles:
          params[FILTER_DISTANCE_IN_MILES] ||
          /* If using a non-location sort option without a distance value,
             set `distance_in_miles` to 0 so that a default location range
             is not applied by the API */
          (NON_LOCATION_SORT_OPTIONS.includes(params[SORT]) ? 0 : null),
        availability: params[FILTER_AVAILABILITY],
        delivery_method_ids: params[FILTER_DELIVERY_METHODS],
        size: params[FILTER_SIZE] || size,
        color: params[FILTER_COLOR],
        coat_type: params[FILTER_COAT],
        tail_type: params[FILTER_TAIL],
        generation: params[FILTER_GENERATION],
        pbo,
        city_slug: citySlug,
        health_tier_levels: params[FILTER_HEALTH_TIER_LEVELS],
        extra_ids: params[FILTER_EXTRAS],
        ...breedSearchAttributeFilter.filters,

        // Puppies only
        ...(isPuppiesTab && {
          gender:
            params[FILTER_GENDER] !== 'any' ? params[FILTER_GENDER] : null,
          price_tier: params[FILTER_PRICE],
          sort: params[SORT],
          age: params[FILTER_AGE]
        })
      })
    )
  };

  const profilesQuery = useResourceQuery(profilesQueryDefinition);

  // tab counts should only reflect filtering based on PBO
  const countsQuery = useResourceQuery({
    ...puppyAndBreederCountsResourceDefinition(breedSlug, {
      pbo
    })
  });

  const profilesMeta = profilesQuery.value.meta;
  const profilesData = profilesQuery.value.data;
  const isLoading = !isFiltersModalOpen && profilesQuery.isLoading;

  const breedersTabCount = countsQuery.value.data.breeders_count;
  const puppiesTabCount = countsQuery.value.data.puppies_count;
  const totalPuppiesCount = profilesMeta.total_puppy_count;
  const totalBreedersCount = profilesMeta.total_breeders_count;

  const totalItemCount =
    (isPuppiesTab ? totalPuppiesCount : totalBreedersCount) || 0;

  const numberOfPages = Math.ceil(totalItemCount / resultsPerPage);
  const distanceInMiles = profilesMeta.distance_in_miles;
  const hasNoItemsDueToFilters =
    !isLoading &&
    totalItemCount === 0 &&
    ((puppiesTabCount > 0 && isPuppiesTab) ||
      (breedersTabCount > 0 && isBreedersTab));

  useEffect(() => {
    if (isPuppiesTab && puppiesTabCount === 0) {
      dispatch(
        pushParams({ [RESULTS_TYPE]: RESULTS_TYPES.BREEDERS }, { hash: true })
      );
    }
  }, [isPuppiesTab, puppiesTabCount]);

  const ref = useRef<any>(null);
  const tabsRef = useRef<any>(null);
  const originParamsRef = useRef<any>(null);

  const scrollToTab = useCallback(() => {
    if (ref.current) {
      scrollTo({ top: ref.current.offsetTop - 60 });
    }
  }, [ref.current]);

  function trackPaginationEvent() {
    sendRudderstackEvent(ANALYTICS_EVENTS.SEARCH_PAGINATED, {
      view:
        resultsType === RESULTS_TYPES.PUPPIES
          ? ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS
          : ANALYTICS_VIEWS.BREEDER_SEARCH_RESULTS,
      breed_id: breedId,
      page_number: currentPage + 1
    });
  }

  function setPage(page: number) {
    dispatch(pushParams({ [PAGE]: page }));

    if (page > currentPage) {
      trackPaginationEvent();
    }
  }

  // update results in DOM only when filters are closed to avoid fire analytics
  // events while filters are still interacted with
  const prevResults = useRef(profilesData);
  const prevResultsType = useRef(resultsType);

  const results = useMemo(() => {
    if (!isFiltersModalOpen || prevResultsType !== resultsType) {
      prevResultsType.current = resultsType;
      prevResults.current = profilesData;
    }
    return prevResults.current;
  }, [profilesData, isFiltersModalOpen, resultsType]);

  const { locationName, locationSource } = useSearchLocation();

  function sendBreedSearchedAnalytics() {
    const view = isPuppiesTab
      ? ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS
      : ANALYTICS_VIEWS.BREEDER_SEARCH_RESULTS;

    if (params.origin_view) {
      originParamsRef.current = removeEmptyValues({
        origin_view: params.origin_view,
        origin_view_feature: params.origin_view_feature,
        origin_view_feature_location: params.origin_view_feature_location
      });

      // remove origin_* from current URL without adding history entry
      const urlWithoutOrigin = removeFromUrl(
        ['origin_view', 'origin_view_feature', 'origin_view_feature_location'],
        window.location.href
      );

      window.history.replaceState(
        window.history.state,
        document.title,
        urlWithoutOrigin // new URL without origin_* params
      );
    } else if (originParamsRef.current !== null) {
      // if origin params don't exist but ref is set, clear the ref
      originParamsRef.current = null;
    }

    setAnalyticsOrigin({ originView: view });

    const formattedPriceAnalytics =
      params[FILTER_PRICE] &&
      puppyPriceRanges
        .filter((p: any) => params[FILTER_PRICE].split(',').includes(p.value))
        .map((p: any) => {
          return { value: p.title, title: p.title };
        });
    const priceAnalytics =
      (Boolean(puppyPriceRanges?.length) &&
        params[FILTER_PRICE] &&
        formattedPriceAnalytics) ||
      null;
    const deliverMethodIds = params[FILTER_DELIVERY_METHODS]?.split(',');
    const deliveryMethods =
      deliverMethodIds &&
      deliveryMethodOptions.reduce((arr: any[], el: any) => {
        if (deliverMethodIds.includes(el.id.toString())) {
          arr.push({
            title: el.name,
            value: el.id
          });
        }
        return arr;
      }, []);

    const eventData = {
      page: 'breed_page',
      breed_id: breedId,
      breed_name: breedName,
      experiments: collectTrackedExperiments(),
      results_size_breeders: totalBreedersCount,
      results_size_available_puppies: totalPuppiesCount,
      page_number: currentPage,
      results_display_type: resultsType,
      filters: {
        postal_code: params[FILTER_POSTAL_CODE],
        state: stateSlug,
        city: cityName,
        distance_in_miles: params[FILTER_DISTANCE_IN_MILES],
        delivery_methods: deliveryMethods,
        size: mapParamsBreedAttributes(params[FILTER_SIZE], breedSizes),
        color: mapParamsBreedAttributes(
          params[FILTER_COLOR],
          breedColors.reduce((c: any[], [, opts]: any) => [...c, ...opts], [])
        ),
        coat_type: mapParamsBreedAttributes(params[FILTER_COAT], breedCoat),
        tail_type: mapParamsBreedAttributes(params[FILTER_TAIL], breedTail),
        generation: mapParamsBreedAttributes(
          params[FILTER_GENERATION],
          breedGeneration
        ),
        health_tier_levels: params[FILTER_HEALTH_TIER_LEVELS],
        ...(breedType === BREED_TYPES.PUREBRED && {
          akc_registered: Boolean(params[FILTER_EXTRAS])
        }),
        ...breedSearchAttributeFilter.filters,
        // Puppies only
        ...(isPuppiesTab && {
          gender: params[FILTER_GENDER],
          availability: params[FILTER_AVAILABILITY],
          price: priceAnalytics,
          age: params[FILTER_AGE]
        })
      },
      // Puppies only
      ...(isPuppiesTab && {
        sort: params[SORT]
      }),
      // Analytics query params
      ...removeEmptyValues({
        origin_view: originParamsRef.current?.origin_view,
        origin_view_feature: originParamsRef.current?.origin_view_feature,
        origin_view_feature_location:
          originParamsRef.current?.origin_view_feature_location,
        search_location_value: locationName,
        search_location_source: locationSource
      })
    };

    sendRudderstackEvent(ANALYTICS_EVENTS.BREED_SEARCHED, eventData);
  }

  useEffect(() => {
    if (profilesQuery.status !== 'success' || isFiltersModalOpen) {
      return;
    }

    sendBreedSearchedAnalytics();
  }, [profilesQuery.status]);

  useEffect(() => {
    const shouldScroll = BROWSE_PARAMS.some(
      param => params[param] !== prevParamsRef.current[param]
    );

    if (shouldScroll) {
      scrollToTab();
    }

    prevParamsRef.current = params;
  }, [params]);

  const isEmptyPage = Boolean(totalItemCount) && currentPage > numberOfPages;

  if (isEmptyPage) {
    const firstPage = removeFromUrl(PAGE, window.location.href);
    window.location.href = firstPage;
    return null;
  }

  return (
    <SearchResultsContext.Provider
      value={{
        sendBreedSearchedAnalytics,
        isLoading: profilesQuery.isLoading,
        searchParams: profilesQueryDefinition.params,
        referrer: window.location.href
      }}
    >
      <div className="PuppyResultsTabbedLayout">
        <ExposedFilters
          className={classnames('bg-sand', {
            pv3: isToggleHidden,
            'pv3 pv6-md': !isToggleHidden
          })}
          resultsType={resultsType}
          tabsContainer={tabsRef.current}
          totalItemCount={totalItemCount}
        />
        <div className="bg-sand ph0-md ph6 mb10">
          <ResultsTypeSelector
            ref={tabsRef}
            className="container-xxl"
            disabled={puppiesTabCount === 0}
          />
        </div>
        <div className="container-xxl">
          <div ref={ref} className="mb18 mb20-lg">
            <LoadingWrapper
              isLoading={isLoading}
              fallback={
                <>
                  <ResultsHeadingSkeleton className="pb7" />
                  <SearchResultsSkeleton
                    className="mt2"
                    resultsType={resultsType}
                    resultsPerPage={resultsPerPage}
                  />
                </>
              }
            >
              {hasNoItemsDueToFilters ? (
                <div className="f2 f3-sm tc bg-near-white br3 ph4 pv4 mt5">
                  <p className="fw-medium">
                    Your current search has no results
                  </p>
                  <p className="mb4">
                    Clear your filters to find puppies and breeders
                  </p>
                  <GDButtonV1
                    className="f2 m-auto"
                    onClick={clearFilters}
                    type="reset"
                  >
                    Clear filters
                  </GDButtonV1>
                </div>
              ) : (
                <ResultsHeading className="pb6" />
              )}
              <SearchResults
                className="mt2"
                results={results}
                resultsType={resultsType}
                distanceInMiles={distanceInMiles}
                totalItemCount={totalItemCount}
                analyticsPageNumber={currentPage}
                Card={isPuppiesTab ? PuppyResultCard : BreederResultCard}
              />
              {overPopulatedBreed && (
                <div className="pt5 pb5 bt bb b--light-gray bw0-l mt5 mb5">
                  <BreederProfileHelpBoxContainer
                    breedName={breedName}
                    breedNamePlural={breedNamePlural}
                  />
                </div>
              )}
              {numberOfPages > 1 && (
                <Paginator
                  className="tc mt6-lg"
                  currentPage={currentPage}
                  itemsPerPage={resultsPerPage}
                  onPageClick={setPage}
                  totalItemCount={totalItemCount}
                  arrows
                />
              )}
            </LoadingWrapper>
          </div>
        </div>
      </div>
      {citySlug && (
        <FeaturedBreedersModule
          className="container-xxl mb6 mb20-lg"
          breedSlug={breedSlug}
          breedNamePlural={breedNamePlural}
          citySlug={citySlug}
          locationTitle={locationTitle}
        />
      )}
      <RelatedBreedsModule className="container-xxl mb6 mb20-lg" />
      {citySlug && (
        <div className="bg-forest-dark-900 white">
          <NearbyOwnerStoriesModule
            className="container-xxl pv12 pv16-md mb8 mb20-lg"
            breedId={breedId}
            citySlug={citySlug}
          />
        </div>
      )}
      <div className="bg-sand">
        <BuyersGuideModule />
        <BreedContentModule />
      </div>
      <div className="mb10 mb20-lg">
        <BuyerAppQRModule />
      </div>
      {citySlug && currentPage === 1 && (
        <FAQModule
          className="container-xxl mb8 mb20-lg"
          breedId={breedId}
          citySlug={citySlug}
        />
      )}
      <SEOFooter />
      {currentPage === 1 && <BreedLocationInfo />}
    </SearchResultsContext.Provider>
  );
}
